import { debounce } from "lodash"
import { useMemo, useState } from "react"

import { DataWithId } from "#customTypes/types"

import useOptimizedSearch from "./useOptimizedSearch"

const useOptimizedSearchWithPagination = <T extends DataWithId>(
  items: T[],
  searchableFields: (keyof T)[],
  pageSize: number,
  defaultSearchTerm = "",
  defaultPage = 1
) => {
  const [currentPage, setCurrentPage] = useState(defaultPage)

  // Use the existing optimized search hook to get all filtered results
  const {
    filteredItems: allFilteredItems,
    onSearch: handleSearchChange,
    searchTerm,
  } = useOptimizedSearch<T>(
    items,
    searchableFields,
    items.length, // Get all results, we'll paginate them ourselves
    defaultSearchTerm
  )

  // Calculate pagination based on filtered results
  const totalPages = Math.ceil(allFilteredItems.length / pageSize)
  const startIndex = (currentPage - 1) * pageSize
  const endIndex = startIndex + pageSize
  const paginatedItems = allFilteredItems.slice(startIndex, endIndex)

  // Reset to page 1 when search term changes
  const onSearch = useMemo(
    () =>
      debounce((searchTerm: string) => {
        setCurrentPage(1)
        handleSearchChange(searchTerm)
      }, 300),
    [handleSearchChange]
  )

  return {
    filteredItems: paginatedItems,
    allFilteredItems,
    onSearch,
    searchTerm,
    currentPage,
    totalPages,
    onPageChange: setCurrentPage,
    totalResults: allFilteredItems.length,
  }
}

export default useOptimizedSearchWithPagination
